import { useToast } from 'hooks/useToast';
import { apiClient } from 'api';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * Interface for basic expectations PATCH payload.
 *
 * Only `assessment_id` is required — every other field is optional so callers can send
 * just the value(s) the user actually edited. The endpoint is a PATCH, so any field left
 * out is preserved server-side; sending the full object on every edit is unnecessary and
 * risks clobbering fields the user didn't touch (e.g. core_responsibilities).
 */
export interface BasicExpectationsEditPayload {
  assessment_id: string;
  employment_type?: string;
  work_mode?: string;
  address?: string | null;
  experience_level?: string;
  years_of_experience?: string;
  experience_environment?: string;
  core_responsibilities?: {
    [key: string]: string | boolean;
  };
  salary_range_term?: string;
  salary_range_currency?: string;
  min_salary_range_amount?: number;
  max_salary_range_amount?: number;
  require_employment_type?: boolean;
  require_work_mode?: boolean;
  require_experience_level?: boolean;
  require_years_of_experience?: boolean;
  require_work_environment?: boolean;
  require_salary_range?: boolean;
  display_salary?: boolean;
  require_cv_upload?: boolean;
  require_portfolio_upload?: boolean;
  assessment?: number;
}

export interface BasicExpectationResponse {
  message: string;
  data: {
    id: number;
    deleted_at: string | null;
    created_at: string;
    updated_at: string;
    employment_type: 'fulltime' | 'parttime' | string;
    work_mode?: 'onsite' | 'remote' | 'hybrid' | 'on_site' | string;
    experience_level: 'entry' | 'mid' | 'senior' | string;
    experience_environment: 'remote' | 'on_site' | 'hybrid' | 'not_important' | string;
    years_of_experience: '0_1' | '2_5' | '5_plus' | string;
    address: string | null;
    country: string | null;
    region: string | null;
    salary_range_term: 'monthly' | 'yearly' | string;
    salary_range_currency: 'USD' | 'NGN' | string;
    min_salary_range_amount: string;
    max_salary_range_amount: string;
    require_employment_type: boolean;
    require_work_mode?: boolean;
    require_experience_level: boolean;
    require_years_of_experience: boolean;
    require_work_environment: boolean;
    require_salary_range: boolean;
    display_salary?: boolean;
    require_cv_upload: boolean;
    require_portfolio_upload?: boolean;
    assessment: number;
    hide_salary_in_form?: boolean;
    require_salary_in_form?: boolean;
    show_salary_in_form?: boolean;
  };
}


/**
 * Hook for fetching and updating basic expectations
 */
export const useBasicExpectationsEdit = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  // Patch basic expectations
  const patchMutation = useMutation({
    mutationFn: async (payload: BasicExpectationsEditPayload) => {
      const response = await apiClient.patch('/assessment/basic-expectations/', payload);
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['basicExpectations', assessmentId] });
      openToast({
        show: true,
        heading: "Edit Successful",
        text: "Basic expectation edited successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.data?.assessment?.[0] || 'Failed to submit basic expectations';
      openToast({
        show: true,
        heading: "Sorry",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    submitBasicExpectations: patchMutation.mutate,
    isSubmitting: patchMutation.isPending,
  };
};


/**
 * Interface for delete basic expectations payload
 */
export interface DeleteBasicExpectationsPayload {
  assessment_id: string;
}

/**
 * Hook for deleting basic expectations
 */
export const useBasicExpectationsDelete = (assessmentId: string | null) => {
  const queryClient = useQueryClient();
  const { openToast } = useToast();

  // Delete basic expectations
  const deleteMutation = useMutation({
    mutationFn: async (payload: DeleteBasicExpectationsPayload) => {
      const response = await apiClient.delete('/assessment/basic-expectations/', { data: payload });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['basicExpectations', assessmentId] });
      openToast({
        show: true,
        heading: "Delete Successful",
        text: "Basic expectation deleted successfully!",
        type: "info",
        timeOut: 5000,
      });
    },
    onError: (err: any) => {
      const errorMessage = err.response?.data?.message || 'Failed to delete basic expectations';
      openToast({
        show: true,
        heading: "Delete Failed",
        text: errorMessage,
        type: "error",
        timeOut: 5000,
      });
    },
  });

  return {
    deleteBasicExpectations: deleteMutation.mutate,
    isDeleting: deleteMutation.isPending,
  };
};

export const useFetchBasicExpectations = (assessmentId: string | null) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ['basicExpectations', assessmentId],
    queryFn: async () => {
      const response = await apiClient.get<BasicExpectationResponse>('/assessment/basic-expectations/', {
        params: { assessment: assessmentId },
      });
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: !!assessmentId,
  });

  return {
    basicExpectation: data,
    isLoading,
    error,
    fetchBasicExpectation: () => queryClient.invalidateQueries({ queryKey: ['basicExpectations', assessmentId] }),
  };
};
